import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('rapidcmc-theme');
    if (savedTheme) {
      return savedTheme;
    }
    
    // Check system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  });
  // Apply theme changes to the DOM
  useEffect(() => {
    const body = document.body;
    
    // Remove existing theme classes
    body.classList.remove('dark-theme', 'light-theme');
    
    // Add the appropriate theme class
    if (theme === 'dark') {
      body.classList.add('dark-theme');
    } else {
      body.classList.add('light-theme');
    }
    
    // Set the theme attribute for compatibility (if needed by other components)
    body.setAttribute('data-pc-theme', theme);
    
    // Set other required Datta Able theme attributes if they don't exist
    if (!body.getAttribute('data-pc-preset')) {
      body.setAttribute('data-pc-preset', 'preset-1');
    }
    if (!body.getAttribute('data-pc-sidebar-caption')) {
      body.setAttribute('data-pc-sidebar-caption', 'true');
    }
    if (!body.getAttribute('data-pc-direction')) {
      body.setAttribute('data-pc-direction', 'ltr');
    }
    
    // Save to localStorage
    localStorage.setItem('rapidcmc-theme', theme);
    
    // Update the global dark_flag if it exists (for compatibility with theme.js)
    if (window.dark_flag !== undefined) {
      window.dark_flag = theme === 'dark';
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if no manual preference is saved
      const savedTheme = localStorage.getItem('rapidcmc-theme');
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  const setLightTheme = () => {
    setTheme('light');
  };

  const setDarkTheme = () => {
    setTheme('dark');
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
